import { advanceHull, type HullBlocker, hullTickTravelMu, type MovablePoint } from '../motion/hull';
import {
  PVP_ARENA_HEIGHT_MU,
  PVP_ARENA_WIDTH_MU,
  PVP_BOOSTER_PUSH_MU_PER_TICK,
  PVP_OBSTACLES,
  PVP_SHIP_RADIUS_MU,
  PVP_SHIP_SPEED_MU_PER_TICK,
} from './balance';

/**
 * One tick of a duel ship's movement.
 *
 * ## What is here, and what is not, since the extraction
 *
 * The arithmetic - the normalised eight-way step, the push out of a pillar, the
 * push out of another hull, the wall clamp - lives in `motion/hull.ts`, which
 * knows nothing about a duel. What is here is the ARENA: its width, its height,
 * its pillars, the reference hull and the base travel per tick. Those five
 * numbers were the only things in the old implementation that were not general,
 * and they are why the open world's room could not call it.
 *
 * Nothing about the duel changed with the move. This function takes the same
 * arguments in the same order, hands them to the same operations in the same
 * sequence, and returns the same integers.
 *
 * ## Why this is not just a method on the simulation
 *
 * The client PREDICTS its own ship so the player sees a key press on the next
 * rendered frame rather than up to 50 ms later. A prediction that used its own
 * speed, its own normalisation or its own collision response would drift from
 * the room within a second and be corrected forever after - and the drift would
 * be invisible in tests, because each side would be self-consistent.
 *
 * So there is exactly one implementation and two callers: `PvpSimulation`,
 * which is the authority, and the browser, which is guessing what the authority
 * will say. The client is still never right by decree - the room's frame always
 * wins - but a guess made with the same arithmetic is right almost always, and
 * wrong by a rounding error rather than by a design difference.
 */

/** A duel point. The neutral shape, named here so no call site had to change. */
export type PvpMovablePoint = MovablePoint;

/** Um casco que bloqueia outro: onde ele está, e quanto ele ocupa. */
export type PvpHullBlocker = HullBlocker;

/**
 * Standing still is returned unchanged, deliberately: a ship that is not moving
 * is not pushed out of anything, which is what keeps a hull that spawned in
 * contact with geometry from being nudged every tick.
 */
const ZERO_DRIFT = Object.freeze({ x: 0, y: 0 });

/** Nenhum. Uma referência partilhada, para o caso comum não alocar. */
const NO_BLOCKERS: readonly PvpHullBlocker[] = Object.freeze([]);

/**
 * The arena's walls, as the neutral step wants them.
 *
 * Built once. `advanceHull` clamps into `[radius, width - radius]`, which is
 * exactly what this function did before the extraction.
 */
const ARENA_BOUNDS = Object.freeze({
  width: PVP_ARENA_WIDTH_MU,
  height: PVP_ARENA_HEIGHT_MU,
});

/**
 * How far one tick of holding a direction moves a hull, at a given speed.
 *
 * The step {@link pvpAdvanceShip} applies, pulled out so that anything which
 * needs to reason about distance uses the SAME arithmetic rather than a second
 * copy of it - `open-world/geometry.ts` derives a quadrant's whole side from
 * it. Integer basis points, truncating division: a caller that recomputed this
 * with a float would disagree with the simulation in the last unit, which is
 * the whole reason this file exists.
 */
export function pvpTickTravelMu(speedBps: number = 10_000): number {
  return hullTickTravelMu(PVP_SHIP_SPEED_MU_PER_TICK, speedBps);
}

/**
 * The furthest a hull could LEGALLY have travelled over `ticks` ticks.
 *
 * ## What this is for
 *
 * Telling a legitimate grouped delivery from a discontinuity. A room that
 * pumped three ticks between two frames has moved a ship up to three ticks'
 * worth, and that is ordinary; a ship that moved further than the rules allow
 * in that interval was TELEPORTED, rebuilt or shoved, and pretending otherwise
 * would smooth a jump the player is entitled to see. See
 * `apps/web/src/pvp/prediction.ts`.
 *
 * ## Why the booster is in the bound
 *
 * Because it is displacement the RULES produce: `drift` is applied by
 * {@link pvpAdvanceShip} before the walls, so a hull standing on a pad really
 * does travel its own step plus the push every tick, without the player asking
 * for it. Leaving it out would make every boosted tick look impossible.
 *
 * ## Why a diagonal does not need a term
 *
 * `movementDirection` normalises, so two keys never travel further than one -
 * the magnitude of a diagonal step is the same step, minus integer rounding.
 * This is therefore an upper bound for every direction, which is what a bound
 * has to be.
 *
 * It is deliberately NOT a tolerance: nothing here may be widened to make a
 * measurement pass. It is the arithmetic of the movement itself, and it moves
 * only when the movement does.
 */
export function pvpMaxTravelMu(ticks: number, speedBps: number = 10_000): number {
  if (ticks <= 0) {
    return 0;
  }
  return ticks * (pvpTickTravelMu(speedBps) + PVP_BOOSTER_PUSH_MU_PER_TICK);
}

export function pvpAdvanceShip(
  from: PvpMovablePoint,
  moveX: number,
  moveY: number,
  /**
   * Everything that moves the hull WITHOUT the player asking, per tick.
   *
   * Boosters, today. Applied before the walls and the obstacles rather than
   * after, so a shove can no more push a ship through a pillar than a key can.
   */
  drift: { readonly x: number; readonly y: number } = ZERO_DRIFT,
  /**
   * Speed as integer basis points of the base. 10 000 is unmodified.
   *
   * A multiplier rather than a second speed constant, so a Speed rune cannot
   * drift away from the number it is meant to be a multiple of. Integer maths
   * throughout: a float here would make the room and the client's prediction
   * disagree in the last bit and correct forever.
   */
  speedBps: number = 10_000,
  /**
   * O raio DESTE casco, em unidades da arena (gate V28G.R).
   *
   * Um argumento e não a constante da arena, porque um casco maior encosta às
   * paredes e aos pilares mais cedo. O valor por omissão é
   * {@link PVP_SHIP_RADIUS_MU} - a nave de referência - o que mantém toda a
   * chamada existente, e toda a previsão de um cliente, exatamente onde estava.
   */
  hullRadius: number = PVP_SHIP_RADIUS_MU,
  /**
   * **Os outros cascos, que este não atravessa** (gate V28H.K).
   *
   * Os obstáculos fixos saíram da arena e o contrato do catálogo (gate V28H.B)
   * decidiu que **as naves tanque exercem o papel de bloqueio físico**. Isto é
   * essa decisão: um casco é expulso de qualquer outro em que entre, exatamente
   * como era expulso de um pilar.
   *
   * **As posições são as do INÍCIO do tique, e isso é o que o torna
   * determinista.** Se cada nave fosse expulsa das posições já actualizadas das
   * anteriores, a ordem em que a sala as percorre decidiria o resultado, e duas
   * re-simulações da mesma janela podiam divergir. Contra o estado de início do
   * tique não há ordem: cada casco vê o mesmo mapa.
   *
   * **Quem se mexe é que é expulso, e nunca o outro.** Uma nave pequena não
   * empurra uma grande - encosta nela. Como o raio sai da vida máxima, uma nave
   * tanque ocupa mais espaço e É a parede, que é literalmente o papel que o
   * contrato lhe deu.
   *
   * Vazio por omissão, o que mantém toda a chamada existente - e a previsão de
   * um cliente que ainda não conheça os outros cascos - exatamente onde estava.
   */
  blockers: readonly PvpHullBlocker[] = NO_BLOCKERS,
): PvpMovablePoint {
  return advanceHull(from, moveX, moveY, {
    baseTravelMu: PVP_SHIP_SPEED_MU_PER_TICK,
    bounds: ARENA_BOUNDS,
    speedBps,
    drift,
    radius: hullRadius,
    boxes: PVP_OBSTACLES,
    blockers,
  });
}
